import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Radio, ShieldCheck, Wifi, Cpu, Globe, CheckCircle2, ExternalLink, Satellite, TriangleAlert, Zap, Activity, Smartphone, QrCode, CreditCard, Gauge, Database, CardSim, RadioTower, Caravan } from "lucide-react";
import { ASSETS, PRODUCTS, APP_LINKS } from "@/data/products";
import SEO from "@/components/SEO";
import BrandedSim from "@/components/BrandedSim";
import AppBadgeLink from "@/components/AppBadgeLink";
import { usePlans } from "@/hooks/usePlans";
import { priceSuffix } from "@/components/PlanCard";

const NetworkBadge = ({ label }) => (
  <div className="font-mono-tech text-[11px] tracking-wider uppercase text-white/60 border border-white/15 px-3 py-1.5 rounded-full">
    {label}
  </div>
);

const Stat = ({ k, label }) => (
  <div className="border-l border-[var(--ptt-line)] pl-4">
    <div className="font-display text-3xl md:text-4xl font-bold tracking-tight">{k}</div>
    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)] mt-1">{label}</div>
  </div>
);

const Home = () => {
  const featured = PRODUCTS.filter((p) => ["multi-sim", "uphone-ptt", "bodycam-pro", "rugged-tablet"].includes(p.id));
  const { loading: plansLoading, plansByCategory } = usePlans();
  const iotPrices = (plansByCategory.iot || []).filter((p) => p.price != null).map((p) => p.price);
  const cheapestIot = iotPrices.length ? Math.min(...iotPrices) : null;
  return (
    <div data-testid="home-page">
      <SEO
        path="/"
        title="Multi-Network SIMs & eSIMs — NZ, AU & 190+ Countries"
        description="PTT NZ — NZ & Australia's multi-network MVNO. Cellular IoT, data pools, fixed wireless, business backup & mobile plans on one SIM with automatic failover."
        keywords="cheapest cellular IoT NZ, multi-network data pools NZ, wholesale MVNO provider New Zealand, flexible M2M data, MVNO New Zealand, MVNO Australia, IoT SIM plans NZ, IoT SIM plans Australia, multi-network SIM New Zealand, multi-network SIM Australia, eSIM New Zealand, eSIM Australia, global roaming SIM 190 countries, worldwide multi-carrier eSIM, white label MVNO New Zealand, business backup internet NZ, critical failover SIM NZ AU, government fleet connectivity NZ, enterprise IoT SIM NZ AU, RV motorhome data plans NZ, mission critical connectivity NZ AU, Spark 2degrees One NZ SIM, Telstra Optus Vodafone native access, network failover SIM, tri-network native access NZ AU"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              { "@type": "Question", name: "Which NZ networks does the PTT NZ SIM use?", acceptedAnswer: { "@type": "Answer", text: "PTT NZ multi-network SIMs and eSIMs connect to Spark NZ, 2degrees Mobile and One NZ. The strongest signal always wins — there is no manual switching." } },
              { "@type": "Question", name: "Does the SIM work in Australia?", acceptedAnswer: { "@type": "Answer", text: "Yes. The same PTT NZ SIM connects natively to Australia's key networks — Telstra, Vodafone and Optus — via direct local network agreements, not a wholesale roaming tier, plus 190+ countries via 600+ wireless carriers globally." } },
              { "@type": "Question", name: "Can I use a PTT NZ SIM or eSIM when travelling or operating overseas?", acceptedAnswer: { "@type": "Answer", text: "Yes. Every PTT NZ SIM and eSIM roams worldwide across 190+ countries via 600+ partner carriers — the same SIM that works across New Zealand and Australia keeps working as your team or device moves internationally." } },
              { "@type": "Question", name: "What happens if a network has an outage?", acceptedAnswer: { "@type": "Answer", text: "The device continuously monitors data access. If data stops flowing on the current carrier due to an outage, disaster or fault, it automatically switches to the next available network with no manual intervention." } },
              { "@type": "Question", name: "Do you supply businesses in Australia, not just New Zealand?", acceptedAnswer: { "@type": "Answer", text: "Yes. While PTT NZ is based in Christchurch, our multi-network SIM platform, PTT radios and hardware are supplied to businesses across both New Zealand and Australia, with global roaming support for teams operating further afield." } },
              { "@type": "Question", name: "Can I manage Real PTT devices in the Connectivity Portal?", acceptedAnswer: { "@type": "Answer", text: "Yes. The Connectivity Portal gives full self-service management of Real PTT accounts and devices alongside your SIMs and eSIMs — one portal for everything." } },
              { "@type": "Question", name: "What's the cheapest cellular IoT plan?", acceptedAnswer: { "@type": "Answer", text: "PTT NZ's entry IoT plan starts from $2.95 NZD/month for 100MB, open term with no contract, connecting to all three New Zealand networks and all three Australian networks on one SIM." } },
              { "@type": "Question", name: "Is PTT NZ a wholesale MVNO provider?", acceptedAnswer: { "@type": "Answer", text: "Yes. PTT NZ operates as a full multi-network MVNO and offers wholesale white-label reseller access to its SIM, IoT data pool and connectivity platform for other businesses to build on." } },
            ],
          },
          cheapestIot ? {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "PTT NZ Cellular IoT SIM",
            description: "Wholesale-grade multi-network cellular IoT SIM connecting Spark NZ, 2degrees, One NZ, Telstra, Vodafone and Optus — open term, no contract.",
            brand: { "@type": "Organization", name: "PTT NZ Limited" },
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "NZD",
              lowPrice: cheapestIot.toFixed(2),
              offerCount: (plansByCategory.iot || []).length || undefined,
              url: "https://pttnz.net/iot-data-pools",
              availability: "https://schema.org/InStock",
            },
          } : null,
        ].filter(Boolean)}
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--ptt-ink)] text-white">
        <div className="absolute inset-0 hairline-grid-dark opacity-60" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-16 lg:pt-24 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="flex items-center gap-2.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-[var(--ptt-teal)] signal-dot" />
              <span className="font-mono-tech text-xs tracking-widest uppercase text-white/70">Multi-network MVNO · NZ &amp; AU</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.02] tracking-tight">
              One SIM. <span className="text-[var(--ptt-teal)]">Every network.</span><br />
              Zero compromise.
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/75 max-w-xl leading-relaxed">
              PTT NZ is a multi-network MVNO delivering mobile, cellular IoT, data pools and fixed wireless on SIMs and eSIMs that connect to <strong className="text-white">Spark NZ, 2degrees and One NZ</strong> — strongest signal always wins. Native access to <strong className="text-white">Telstra, Vodafone and Optus</strong> in Australia, plus 190+ countries globally via roaming partners. If one network fails, your device automatically switches to the next. PTT radios, body cameras and IoT hardware run on the same platform.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/iot-data-pools" data-testid="hero-cta-iot" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2.5">
                Cellular IoT from $2.95/mo <ArrowRight size={14} />
              </Link>
              <Link to="/solutions" data-testid="hero-cta-solutions" className="px-6 py-3.5 text-sm font-semibold inline-flex items-center gap-2.5 border border-white/30 hover:border-[var(--ptt-teal)] hover:text-[var(--ptt-teal)] transition-colors">
                What we offer <ArrowRight size={14} />
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-3" data-testid="hero-network-badges">
              <NetworkBadge label="One NZ" />
              <NetworkBadge label="Spark NZ" />
              <NetworkBadge label="2degrees" />
              <NetworkBadge label="AU · Global" />
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-4 bg-[var(--ptt-teal)]/10 blur-3xl rounded-full" />
            <div className="relative aspect-[4/5] border border-white/15 overflow-hidden">
              <img src="https://images.unsplash.com/photo-1679091131773-50f3cd76d7c3?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Multi-network cell tower infrastructure powering PTT NZ's MVNO SIMs" className="w-full h-full object-cover" />
              <div className="absolute top-4 left-4 font-mono-tech text-[10px] tracking-widest uppercase text-white/80 bg-[var(--ptt-ink)]/80 backdrop-blur-sm px-2.5 py-1.5 border border-white/15">
                LIVE · 41.2865°S, 174.7762°E

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-[var(--ptt-paper)] border-y border-[var(--ptt-line)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          <Stat k="3" label="NZ Networks · 1 SIM" />
          <Stat k="190+" label="Countries · Global roaming" />
          <Stat k="600+" label="Wireless carriers" />
          <Stat k="24/7" label="Mission-critical support" />
        </div>
      </section>

      {/* LIVE IOT PRICING — hero commercial offer */}
      <section className="bg-[var(--ptt-ink)] border-b border-white/10" data-testid="home-iot-pricing-hero">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-12 lg:py-14">
          <div className="border border-white/15 bg-white/[0.04] p-6 lg:p-9 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="eyebrow text-[var(--ptt-teal)]">Wholesale-grade cellular IoT</div>
              <h2 className="font-display text-2xl lg:text-3xl font-bold mt-2 leading-[1.1] text-white">Cellular IoT &amp; Data Pools, priced to scale from one sensor to one thousand.</h2>
              <div className="flex flex-wrap gap-2 mt-5" data-testid="home-iot-pricing-badges">
                {["Open term", "No contract", "Tri-network NZ", "Tri-network AU"].map((b) => (
                  <span key={b} className="font-mono-tech text-[10px] uppercase tracking-widest px-2.5 py-1.5 border border-white/20 text-white/70 inline-flex items-center gap-1.5"><CheckCircle2 size={11} className="text-[var(--ptt-teal)]" />{b}</span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5 flex items-center gap-6">
              <div className="shrink-0">
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">From</div>
                {plansLoading ? (
                  <div className="h-12 w-28 bg-white/10 animate-pulse mt-1" />
                ) : (
                  <div className="font-display text-5xl font-bold text-white leading-none mt-1" data-testid="home-iot-live-price">
                    ${cheapestIot != null ? cheapestIot.toFixed(2) : "—"}<span className="text-sm font-normal text-white/50">/mo</span>
                  </div>
                )}
                <div className="text-xs text-white/50 mt-1">100MB · NZ &amp; AU</div>
              </div>
              <div className="flex flex-col gap-2.5 w-full">
                <Link to="/iot-data-pools" data-testid="home-iot-pricing-cta" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center justify-center gap-2">See IoT &amp; Data Pools <ArrowRight size={14} /></Link>
                <Link to="/plans#iot" data-testid="home-iot-pricing-cta-plans" className="text-center text-xs font-semibold text-white/60 hover:text-white transition-colors">All live pricing on Plans →</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AUTO-FAILOVER — the mission-critical differentiator */}
      <section className="relative overflow-hidden bg-white border-b border-[var(--ptt-line)]">
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--ptt-ink)] bg-[var(--ptt-teal)]/15 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--ptt-teal-deep)] signal-dot" />
                <span className="font-mono-tech text-[10px] uppercase tracking-widest">Never dark</span>
              </div>
              <h2 className="font-display text-4xl lg:text-5xl font-bold leading-[1.05]">
                When one network drops,<br />
                <span className="text-[var(--ptt-teal-deep)]">the next one takes over.</span>
              </h2>
              <p className="mt-5 text-[var(--ptt-ink-soft)] leading-relaxed max-w-md">
                Every PTT NZ SIM continuously monitors real data access — not just signal bars. The moment data stops flowing on the current carrier — network outage, natural disaster, tower fault, or congestion — the device automatically switches to the next available network. No manual intervention. No user action. No dropped operations.
              </p>
              <p className="mt-4 text-[var(--ptt-ink-soft)] leading-relaxed max-w-md">
                This is the difference between <em className="not-italic font-semibold">connected</em> and <em className="not-italic font-semibold">working</em>. It&apos;s why utilities, transport, security and emergency services trust us on their most critical devices.
              </p>
              <Link to="/network-outage" data-testid="home-outage-cta" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold signal-underline text-[var(--ptt-teal-deep)]">Network down right now? Get connected in 60 seconds <ArrowRight size={13} /></Link>
            </div>

            <div className="lg:col-span-7">
              {/* Network availability visual */}
              <div className="border border-[var(--ptt-ink)] bg-[var(--ptt-paper)] p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">Data-availability watchdog</div>
                  <div className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--ptt-teal-deep)] signal-dot" /> LIVE
                  </div>
                </div>

                <div className="space-y-3">
                  {[
                    { carrier: "Spark NZ", status: "primary", detail: "Data flowing · 47ms" },
                    { carrier: "2degrees", status: "standby", detail: "Available · standby" },
                    { carrier: "One NZ", status: "standby", detail: "Available · standby" },
                    { carrier: "Telstra AU", status: "standby-au", detail: "AU native · standby" },
                    { carrier: "Vodafone AU", status: "standby-au", detail: "AU native · standby" },
                    { carrier: "Optus AU", status: "standby-au", detail: "AU native · standby" },
                    { carrier: "600+ global carriers", status: "global", detail: "190 countries" },
                  ].map((n) => {
                    const dotClass =
                      n.status === "primary" ? "bg-[var(--ptt-teal-deep)] signal-dot"
                      : n.status === "global" ? "bg-[var(--ptt-teal)]/50"
                      : "bg-[var(--ptt-muted)]/40";
                    return (
                      <div key={n.carrier} className="flex items-center justify-between py-2.5 border-b border-[var(--ptt-line)] last:border-b-0">
                        <div className="flex items-center gap-3">
                          <span className={`w-2 h-2 rounded-full ${dotClass}`} />
                          <span className="font-display font-semibold text-sm">{n.carrier}</span>
                        </div>
                        <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">{n.detail}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 pt-5 border-t border-[var(--ptt-line)] grid sm:grid-cols-3 gap-4">
                  <div>
                    <div className="flex items-center gap-1.5 mb-1"><Activity size={13} className="text-[var(--ptt-teal-deep)]" /><span className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">Monitors</span></div>
                    <div className="font-display text-sm font-bold">Real data access</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 mb-1"><Zap size={13} className="text-[var(--ptt-teal-deep)]" /><span className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">Switchover</span></div>
                    <div className="font-display text-sm font-bold">Automatic · no user input</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 mb-1"><ShieldCheck size={13} className="text-[var(--ptt-teal-deep)]" /><span className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">Survives</span></div>
                    <div className="font-display text-sm font-bold">Outages · disasters · faults</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="eyebrow">Why PTT NZ</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold mt-3 leading-[1.05]">
              When your comms have to <em className="not-italic text-[var(--ptt-teal-deep)]">just work.</em>
            </h2>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
            {[
              { icon: <Globe size={22} />, title: "Multi-network MVNO", text: "One SIM across Spark, 2degrees, One NZ, plus Australia's major carriers and 190+ countries worldwide — strongest signal always wins." },
              { icon: <Database size={22} />, title: "Cellular IoT & Data Pools", text: "Per-SIM IoT plans or a shared Data Pool across your whole fleet — from a single sensor to thousands of devices, open term, no contract." },
              { icon: <ShieldCheck size={22} />, title: "Mission-critical SLA", text: "Connectivity engineered for first-response, utilities, transport and field operations — built for uptime and redundancy." },
              { icon: <Radio size={22} />, title: "PoC radios + body cams", text: "Cellular Push-to-Talk radios, ATEX-rated handsets and evidence-grade body cameras — all working over our SIM platform." },
            ].map((f) => (
              <div key={f.title} className="group relative p-6 bg-white border border-[var(--ptt-line)] hover:border-[var(--ptt-ink)] transition-colors" data-testid={`why-card-${f.title.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="w-11 h-11 grid place-items-center bg-[var(--ptt-teal)]/15 text-[var(--ptt-ink)]">{f.icon}</div>
                <h3 className="font-display text-xl font-bold mt-5">{f.title}</h3>
                <p className="text-sm text-[var(--ptt-muted)] mt-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: f.text }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIM SPOTLIGHT */}
      <section className="bg-[var(--ptt-ink)] text-white relative overflow-hidden">
        <div className="absolute inset-0 hairline-grid-dark opacity-50" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative aspect-square bg-white/5 border border-white/10 grid place-items-center p-6 overflow-hidden">
              <BrandedSim variant="sim" className="w-full h-auto drop-shadow-2xl" />
              <div className="absolute -bottom-3 -right-3 bg-[var(--ptt-teal)] text-[var(--ptt-ink)] font-display font-bold px-4 py-2 text-sm">GLOBAL · MULTI-NETWORK</div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="eyebrow text-white/50">Our biggest asset</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold mt-3 leading-[1.05]">
              One mission-critical SIM.<br />
              <span className="text-[var(--ptt-teal)]">Strongest signal wins</span> — NZ, AU, worldwide.
            </h2>
            <p className="mt-5 text-white/75 text-base leading-relaxed max-w-xl">
              No swapping cards. Just set an initial single APN and the device will connect freely between Spark NZ, 2degrees and One NZ — automatically locking to the strongest available signal. Same SIM extends natively across Australia&apos;s key carriers and 190+ countries via 600+ wireless partners, so devices stay connected wherever your operation goes. Drop one in your own hardware and unlock true network redundancy.
            </p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-3 text-sm text-white/85">
              {["3 NZ networks · strongest signal wins", "Native AU network access", "190+ countries · 600+ carriers", "No APN swap after first setup", "Physical SIM or eSIM", "Central management portal"].map((p) => (
                <li key={p} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[var(--ptt-teal)]" /> {p}</li>
              ))}
            </ul>
            <div className="mt-8 flex gap-3 flex-wrap">
              <a href="https://portal.pttnz.net" target="_blank" rel="noopener noreferrer" data-testid="sim-cta-portal" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2">Manage in Connectivity Portal <ExternalLink size={13} /></a>
              <Link to="/contact" data-testid="sim-cta-contact" className="border border-white/30 hover:border-[var(--ptt-teal)] hover:text-[var(--ptt-teal)] px-5 py-3 text-sm font-semibold transition-colors">Request a trial SIM</Link>
            </div>
          </div>
        </div>
      </section>

      {/* PLANS TEASER — MVNO product lines, moved ahead of hardware */}
      <section className="bg-[var(--ptt-paper)] border-y border-[var(--ptt-line)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-24">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <div className="eyebrow">MVNO plans</div>
              <h2 className="font-display text-4xl lg:text-5xl font-bold mt-3 leading-[1.05]">Mobile, Fixed Wireless, Cellular IoT &amp; Data Pools — one network advantage.</h2>
              <p className="mt-4 text-[var(--ptt-ink-soft)] leading-relaxed">Every plan connects across Spark NZ, One NZ and 2degrees, plus native access to Australia&apos;s Telstra, Optus and Vodafone.</p>
            </div>
            <Link to="/plans" data-testid="home-plans-view-all" className="ghost-button px-5 py-3 text-sm font-semibold inline-flex items-center gap-2 shrink-0">View all plans <ArrowRight size={14} /></Link>
          </div>
          {(() => {
            const renderCard = (c) => {
              const catPlans = plansByCategory[c.category] || [];
              const pricedPlans = catPlans.filter((p) => p.price != null);
              const cheapestPlan = pricedPlans.length ? pricedPlans.reduce((min, p) => (p.price < min.price ? p : min), pricedPlans[0]) : null;
              const isAnnual = cheapestPlan && cheapestPlan.min_months === 12;
              return (
                <Link key={c.href} to={c.href} data-testid={`home-plans-card-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="relative p-4 lg:p-5 bg-white border border-[var(--ptt-line)] hover:border-[var(--ptt-ink)] transition-colors group">
                  {isAnnual && (
                    <div data-testid={`home-plans-card-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}-annual-badge`} className="absolute top-3 right-3 font-mono-tech text-[8px] uppercase tracking-widest text-amber-700 bg-amber-100 px-2 py-1">Billed annually</div>
                  )}
                  <div className={`w-8 h-8 grid place-items-center transition-colors ${c.accent}`}>
                    <c.icon size={16} />
                  </div>
                  <div className="font-mono-tech text-[9px] uppercase tracking-widest text-[var(--ptt-muted)] mt-3">From</div>
                  {plansLoading ? (
                    <div className="h-7 w-16 bg-[var(--ptt-line)] animate-pulse mt-1.5" />
                  ) : cheapestPlan ? (
                    <div className="font-display text-2xl font-bold mt-1">${cheapestPlan.price.toFixed(2)}<span className="text-xs font-normal text-[var(--ptt-muted)]">{priceSuffix(cheapestPlan.min_months)}</span></div>
                  ) : (
                    <div className="font-display text-base font-bold mt-1.5 text-[var(--ptt-teal-deep)]">Contact us</div>
                  )}
                  <div className="font-display text-base font-bold mt-3">{c.label}</div>
                  <div className="text-xs text-[var(--ptt-muted)] mt-1">{c.note}</div>
                  <div className="mt-4 text-sm font-semibold signal-underline inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">See plans <ArrowRight size={13} /></div>
                </Link>
              );
            };
            const topRow = [
              { href: "/iot-data-pools", category: "iot", label: "Cellular IoT", note: "Open term · no contract", icon: Cpu, accent: "bg-[var(--ptt-teal)]/15 text-[var(--ptt-teal-deep)] group-hover:bg-[var(--ptt-teal)]/25" },
              { href: "/plans#data-pools", category: "data-pools", label: "Data Pools", note: "Shared across your fleet", icon: Database, accent: "bg-blue-500/15 text-blue-600 group-hover:bg-blue-500/25" },
              { href: "/plans#mobile", category: "mobile", label: "Mobile Data", note: "Prepaid · pay as you go", icon: CardSim, accent: "bg-amber-500/15 text-amber-600 group-hover:bg-amber-500/25" },
              { href: "/plans#fixed-wireless", category: "fixed-wireless", label: "Fixed Wireless", note: "Business · month-to-month", icon: RadioTower, accent: "bg-orange-500/15 text-orange-600 group-hover:bg-orange-500/25" },
            ];
            const bottomRow = [
              { href: "/rv-motorhome", category: "rv-motorhome", label: "RV & Motorhome", note: "For life on the road", icon: Caravan, accent: "bg-rose-500/15 text-rose-600 group-hover:bg-rose-500/25" },
              { href: "/plans#business-backup", category: "business-backup", label: "Business Backup", note: "Redundant internet failover", icon: ShieldCheck, accent: "bg-slate-700/15 text-slate-700 group-hover:bg-slate-700/25" },
            ];
            return (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
                  {topRow.map(renderCard)}
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 lg:justify-center">
                  <div className="lg:col-start-2 lg:col-span-2 grid sm:grid-cols-2 gap-4">
                    {bottomRow.map(renderCard)}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* FEATURED PRODUCTS — hardware, secondary to MVNO plans */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="eyebrow">Hardware</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold mt-3 leading-[1.05]">Built for the field.</h2>
          </div>
          <Link to="/products" className="text-sm font-semibold inline-flex items-center gap-2 signal-underline" data-testid="featured-view-all">See supported hardware <ArrowRight size={14} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {featured.map((p) => (
            <Link key={p.id} to={`/products#${p.category}`} className="group bg-white border border-[var(--ptt-line)] hover:border-[var(--ptt-ink)] transition-colors p-4 flex flex-col" data-testid={`featured-product-${p.id}`}>
              <div className="aspect-square bg-[var(--ptt-paper)] grid place-items-center overflow-hidden p-3">
                {p.id === "multi-sim" ? (
                  <BrandedSim variant="simple" className="w-full h-auto group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <img src={p.image} alt={p.name} className="max-w-[78%] max-h-[78%] object-contain group-hover:scale-105 transition-transform duration-500" />
                )}
              </div>
              <div className="font-mono-tech text-[10px] tracking-widest uppercase text-[var(--ptt-muted)] mt-4">{p.category.replace(/-/g, " ")}</div>
              <div className="font-display text-base font-bold mt-1.5 leading-snug">{p.name}</div>
              <div className="text-xs text-[var(--ptt-muted)] mt-1.5 line-clamp-2">{p.tagline}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* PLATFORMS — customer portals */}
      <section className="bg-[var(--ptt-ink)] text-white relative overflow-hidden">
        <div className="absolute inset-0 hairline-grid-dark opacity-50" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-24">
          <div className="max-w-3xl">
            <div className="eyebrow text-white/50">Platforms</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold mt-3 leading-[1.05]">
              Two purpose-built portals.<br />
              <span className="text-[var(--ptt-teal)]">One operational pane of glass.</span>
            </h2>
            <p className="mt-5 text-white/70 max-w-xl text-base leading-relaxed">
              Everything you need to run your comms and stay ahead of NZ road disruption — self-serve, real-time, no phone-tag with support.
            </p>
          </div>

          <div className="mt-12 grid lg:grid-cols-2 gap-6">
            {/* Connectivity Portal */}
            <a href="https://portal.pttnz.net" target="_blank" rel="noopener noreferrer" data-testid="platform-card-connectivity" className="group relative bg-white/[0.04] border border-white/15 hover:border-[var(--ptt-teal)] transition-colors p-7 lg:p-8">
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 grid place-items-center bg-[var(--ptt-teal)]/20 border border-[var(--ptt-teal)]/40">
                  <Satellite size={20} className="text-[var(--ptt-teal)]" />
                </div>
                <ExternalLink size={16} className="text-white/40 group-hover:text-[var(--ptt-teal)] transition-colors" />
              </div>
              <h3 className="font-display text-2xl font-bold mt-6">Connectivity Portal</h3>
              <p className="text-white/60 mt-2 text-sm leading-relaxed">Manage SIMs &amp; eSIMs, purchase plans, and control your PTT radio fleet — all from one place.</p>
              <div className="mt-6 space-y-2 text-sm">
                {[
                  "Order SIMs, eSIMs and data plans instantly",
                  "Monitor usage, run diagnostics, activate/suspend",
                  "Create PTT users, devices and talk groups",
                  "ICCID linking — SIM ↔ PTT device in one view",
                  "Worldwide roaming visibility & controls",
                ].map((b) => (
                  <div key={b} className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[var(--ptt-teal)] mt-1 shrink-0" /><span className="text-white/80">{b}</span></div>
                ))}
              </div>
              <div className="mt-6 font-mono-tech text-[10px] uppercase tracking-widest text-white/40 truncate">portal.pttnz.net</div>
            </a>

            {/* PTT NZ Alerts */}
            <a href="https://alerts.pttnz.net" target="_blank" rel="noopener noreferrer" data-testid="platform-card-alerts" className="group relative bg-white/[0.04] border border-white/15 hover:border-[var(--ptt-teal)] transition-colors p-7 lg:p-8">
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 grid place-items-center bg-[var(--ptt-teal)]/20 border border-[var(--ptt-teal)]/40">
                  <TriangleAlert size={20} className="text-[var(--ptt-teal)]" />
                </div>
                <ExternalLink size={16} className="text-white/40 group-hover:text-[var(--ptt-teal)] transition-colors" />
              </div>
              <h3 className="font-display text-2xl font-bold mt-6">PTT NZ Alerts</h3>
              <p className="text-white/60 mt-2 text-sm leading-relaxed">Real-time NZ highway incidents, closures and traffic events — plus load pilot scheduling for oversize moves. Direct NZTA feed.</p>
              <div className="mt-6 space-y-2 text-sm">
                {[
                  "Live NZTA Waka Kotahi Traffic feed",
                  "3-tier severity: Critical · Urgent · Info",
                  "Push, email + SMS multi-channel alerts",
                  "Load pilot scheduling & waypoint ETAs",
                  "50MAX / HPMV / bridge restriction layers",
                ].map((b) => (
                  <div key={b} className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[var(--ptt-teal)] mt-1 shrink-0" /><span className="text-white/80">{b}</span></div>
                ))}
              </div>
              <div className="mt-6 font-mono-tech text-[10px] uppercase tracking-widest text-white/40 truncate">alerts.pttnz.net</div>
            </a>
          </div>

          {/* Built-by-Kiwis callout */}
          <div className="mt-10 border border-white/15 bg-white/[0.03] p-6 lg:p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6" data-testid="kiwi-built-callout">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[var(--ptt-teal)] signal-dot" />
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-teal)]">Made in Aotearoa</span>
              </div>
              <h3 className="font-display text-2xl lg:text-3xl font-bold leading-tight">
                Both platforms custom-built in-house — <span className="text-[var(--ptt-teal)]">by Kiwis, for Kiwis</span> (and the world).
              </h3>
              <p className="text-white/70 mt-3 text-sm leading-relaxed">
                We designed and built the Connectivity Portal and PTT NZ Alerts platform ourselves — so both are engineered around how Kiwi operators actually work. That also means we&apos;re free to shape them around you. Got an idea for a feature? Tell us — we can usually integrate it into either platform quickly.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="kiwi-built-suggest-cta"
              className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2 shrink-0"
            >
              Suggest a feature <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* REAL PTT INTEGRATION */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="eyebrow">Real PTT integration</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold mt-3 leading-[1.05]">
              Got a device that works with <span className="text-[var(--ptt-teal-deep)]">Real PTT?</span>
            </h2>
            <p className="mt-5 text-[var(--ptt-ink-soft)] leading-relaxed max-w-xl">
              Our Connectivity Portal gives you full self-service management of your Real PTT accounts and devices — right alongside your SIMs and eSIMs. Provision users, create and assign talk groups, link devices to their SIM via ICCID, monitor data usage, and swap plans, all from one dashboard.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2.5 text-sm text-[var(--ptt-ink-soft)]">
              {[
                "Create & manage Real PTT users",
                "Assign devices to talk groups",
                "ICCID linking — device ↔ SIM in one place",
                "Same portal as your SIMs & plans",
                "Self-service · no support tickets",
                "Instant activation & deactivation",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2"><CheckCircle2 size={15} className="text-[var(--ptt-teal-deep)] mt-0.5 shrink-0" /><span>{b}</span></li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="https://portal.pttnz.net" target="_blank" rel="noopener noreferrer" data-testid="realptt-cta-portal" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2">Open Connectivity Portal <ExternalLink size={13} /></a>
              <Link to="/contact" data-testid="realptt-cta-contact" className="ghost-button px-5 py-3 text-sm font-semibold inline-flex items-center gap-2">Get set up <ArrowRight size={14} /></Link>
            </div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative border border-[var(--ptt-ink)] bg-white p-6 lg:p-8">
              <div className="absolute -top-3 left-6 bg-[var(--ptt-ink)] text-white px-3 py-1 font-mono-tech text-[10px] uppercase tracking-widest">
                Real PTT · One portal
              </div>
              <div className="pt-4 space-y-2.5">
                {[
                  { l: "PTT user", v: "operator-047", tag: "Active" },
                  { l: "Device", v: "Uphone-RG500 · IMEI 3591…", tag: "Online" },
                  { l: "Linked ICCID", v: "8964 0212 3456 7890", tag: "Multi-net" },
                  { l: "Talk group", v: "SH1-North · Ops · Dispatch", tag: "3 groups" },
                  { l: "Data plan", v: "10 GB · pooled · global", tag: "Active" },
                ].map((r) => (
                  <div key={r.l} className="border border-[var(--ptt-line)] px-4 py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-mono-tech text-[9px] uppercase tracking-widest text-[var(--ptt-muted)]">{r.l}</div>
                      <div className="font-display font-bold text-sm truncate">{r.v}</div>
                    </div>
                    <span className="font-mono-tech text-[10px] uppercase tracking-widest bg-[var(--ptt-teal)]/20 px-2 py-1 shrink-0">{r.tag}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MOBILE APP */}
      <section className="bg-[var(--ptt-ink)] text-white relative overflow-hidden" data-testid="home-mobile-app-section">
        <div className="absolute -left-24 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-[var(--ptt-teal)]/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28 relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow text-[var(--ptt-teal)]">PTT NZ App</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold mt-3 leading-[1.05]">
              Your SIMs, eSIMs & plans — <span className="text-[var(--ptt-teal)]">right from your pocket.</span>
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed max-w-xl">
              Buy an eSIM and get connected in seconds. Install it on your phone with a single tap, or generate a QR code / manual setup code to activate it on another device — no store visit, no waiting. Manage every plan, top-up and data allowance from one app.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                "Purchase an eSIM and activate with one click",
                "Install instantly, or scan a QR code / enter a manual code on another device",
                "Track live data usage across all your SIMs & eSIMs",
                "Manage plans, top-ups and renewals on the go",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-white/85">
                  <CheckCircle2 size={17} className="text-[var(--ptt-teal)] mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-9 flex flex-wrap items-center gap-4" data-testid="home-app-badges">
              <AppBadgeLink
                href={APP_LINKS.appStore}
                badgeSrc={ASSETS.appStoreBadge}
                badgeAlt="Download on the App Store"
                storeName="the App Store"
                testId="home-app-store-badge"
                className="inline-block transition-transform hover:scale-[1.04]"
                imgClassName="h-12 w-auto"
              />
              <AppBadgeLink
                href={APP_LINKS.googlePlay}
                badgeSrc={ASSETS.googlePlayBadge}
                badgeAlt="Get it on Google Play"
                storeName="Google Play"
                testId="home-google-play-badge"
                className="inline-block transition-transform hover:scale-[1.04]"
                imgClassName="h-12 w-auto"
              />
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col items-center" data-testid="home-app-visual">
            <div className="relative">
              <div className="absolute inset-0 bg-[var(--ptt-teal)]/25 blur-3xl rounded-full scale-90" />
              <img
                src={ASSETS.appIcon}
                alt="PTT NZ mobile app icon"
                className="relative w-48 lg:w-56 rounded-[2rem] shadow-2xl ring-1 ring-white/10"
                data-testid="home-app-icon"
              />
            </div>
            <div className="mt-8 grid grid-cols-2 gap-2.5 w-full max-w-sm" data-testid="home-app-feature-cards">
              {[
                { icon: CreditCard, label: "Buy an eSIM" },
                { icon: Smartphone, label: "One-tap install" },
                { icon: QrCode, label: "QR / manual code" },
                { icon: Gauge, label: "Live usage" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="bg-white/[0.06] border border-white/10 px-3.5 py-3 flex items-center gap-2.5">
                  <Icon size={16} className="text-[var(--ptt-teal)] shrink-0" />
                  <span className="text-xs font-semibold text-white/85">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMPLETE CONNECTIVITY STRIP */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-20 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 bg-[var(--ptt-paper)] border border-[var(--ptt-line)] p-8 lg:p-10">
            <div className="eyebrow">Complete connectivity</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold mt-3 leading-[1.1]">Fibre. Wi-Fi. Wireless links. <span className="text-[var(--ptt-teal-deep)]">All from one team.</span></h2>
            <p className="text-[var(--ptt-muted)] mt-4 max-w-xl text-sm leading-relaxed">
              PTT NZ also delivers a range of business fibre broadband plans, full network design and configuration, custom wireless site-to-site links, WISP services, and Wi-Fi deployments — from small businesses to large-scale warehouses and logistics firms. Vendor-agnostic: MikroTik, Ubiquiti, Peplink and more. Competitive pricing, 24/7 NZ-based support, and tickets answered within 3 hours.
            </p>
            <div className="mt-6">
              <Link to="/solutions" data-testid="home-connectivity-cta" className="text-sm font-semibold inline-flex items-center gap-1.5 signal-underline">See network services <ArrowRight size={14} /></Link>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3" data-testid="home-sla-cards">
            <div className="bg-[var(--ptt-ink)] text-white p-6 flex flex-col justify-between">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Support</div>
              <div className="font-display text-5xl font-bold leading-none mt-3">24<span className="text-[var(--ptt-teal)]">/</span>7</div>
              <div className="text-xs text-white/70 mt-3">NZ-based · always-on</div>
            </div>
            <div className="bg-[var(--ptt-teal)] text-[var(--ptt-ink)] p-6 flex flex-col justify-between">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-ink)]/70">Response SLA</div>
              <div className="font-display text-5xl font-bold leading-none mt-3">≤ 3h</div>
              <div className="text-xs text-[var(--ptt-ink)]/80 mt-3">to every new ticket</div>
            </div>
            <div className="col-span-2 border border-[var(--ptt-line)] bg-white p-5">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)] mb-3">Engineered with</div>
              <div className="flex flex-wrap gap-2">
                {["MikroTik", "Ubiquiti", "Peplink", "Cambium", "Cisco Meraki"].map((b) => (
                  <span key={b} className="px-2.5 py-1 border border-[var(--ptt-line)] text-[11px] font-display font-semibold">{b}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mb-24">
        <div className="relative border border-[var(--ptt-ink)] p-8 lg:p-14 bg-white overflow-hidden">
          <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-[var(--ptt-teal)]/20 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="eyebrow">Resellers</div>
              <h3 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight">Help deploy multi-network access across Aotearoa.</h3>
              <p className="text-[var(--ptt-muted)] mt-4 max-w-2xl">Our door is always open to resellers passionate about mission-critical comms, broadband redundancy and fibre broadband solutions. Margins, training, and full technical support included.</p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link to="/resellers" data-testid="home-reseller-cta" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2">Become a reseller <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
